import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormControl } from '@angular/forms';

import { BehaviorSubject, combineLatest, merge, Observable, Subject } from 'rxjs';
import { filter, map, shareReplay, skip, take, takeUntil, withLatestFrom } from 'rxjs/operators';

import { CommonModule } from '@angular/common';
import { flatten, isEqual, uniqWith } from 'lodash-es';

import { ShAutocompleteComponent } from '../../../form-controls/components/_core/autocomplete/autocomplete.component';
import { ShIndeterminateDirective } from '../../../form-controls/directives/indeterminate.directive';
import { FormSelectOption } from '../../../form-controls/entities/form-select-option.entity';
import { FormValue, FormValueState } from '../../../form-controls/entities/form-value.entity';
import { MultiFormSelectOptions } from '../../../form-controls/entities/multi-form-select-options.entity';
import { ShActionItemComponent } from '../action-item/action-item.component';
import { ShDroppanelComponent } from '../droppanel/droppanel.component';
import { ShIconModule } from '../icon/icon.module';
import { ShChildIndexDirective } from '../../directives/child-index/child-index';
import { BaseDroppanelSelectComponent } from '../base-droppanel-select/base-droppanel-select.abstract';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    standalone: true,
    selector: 'sh-droppanel-select',
    templateUrl: './droppanel-select.component.html',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        ShActionItemComponent,
        ShIconModule,
        ShDroppanelComponent,
        ShIndeterminateDirective,
        ShAutocompleteComponent,
        ShChildIndexDirective,
        TranslateModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ShDroppanelSelectComponent<T = string> extends BaseDroppanelSelectComponent<T> {
    /* Autocomplete properties */
    public initialAutocompleteOptions$: Subject<MultiFormSelectOptions<T>> = new Subject<MultiFormSelectOptions<T>>();
    public selectedSuggestions$: BehaviorSubject<FormSelectOption<T>[]> = new BehaviorSubject<FormSelectOption<T>[]>(
        []
    );

    public autocompleteInputControl: UntypedFormControl;
    public autocomplete = false;

    /* Options */
    public filteredOptions$!: Observable<FormSelectOption<T>[]>;
    public cappedFilteredOptions$!: Observable<FormSelectOption<T>[]>;

    /* Extra items */
    private readonly extraItemCount$: BehaviorSubject<number> = new BehaviorSubject(0);

    /* Messages */
    @Input() listCappedMsg = this.translateService.translate('ui.load-more');

    /* Specific inputs */
    @Input() itemCapCount = 50;
    @Input() noResultsActionItem?: any;
    @Input() autocompleteFct?: (term: string) => Observable<string[]>;

    @Input() set options(value: MultiFormSelectOptions<T>) {
        if (value) {
            this.multiFormSelectOptions$.next(value);
        }
    }

    @Output() searchTerm: EventEmitter<string> = new EventEmitter<string>();
    @Output() shownItemCount: EventEmitter<number> = new EventEmitter<number>();
    @Output() selectionState: EventEmitter<FormValue<FormSelectOption<T>>[]> = new EventEmitter<
        FormValue<FormSelectOption<T>>[]
    >();

    constructor(formBuilder: UntypedFormBuilder) {
        super(formBuilder);
    }

    protected override onInit(): void {
        this.autocomplete = !!this.autocompleteFct;
    }

    protected override onDestroy(): void {
        this.initialAutocompleteOptions$.complete();
        this.extraItemCount$.complete();
        this.selectedSuggestions$.complete();
    }

    protected override initControls(): void {
        super.initControls();
        this.autocompleteInputControl = this.formBuilder.control('');
    }

    protected override initSourceStreams(): void {
        super.initSourceStreams();
        this.filteredOptions$ = this.buildFilteredOptionsStream();
        this.cappedFilteredOptions$ = this.buildCappedFilteredOptionsStream();
    }

    protected override initStateStreams(): void {
        super.initStateStreams();
        this.showExtraItemsLabelIndicator$ = combineLatest([this.filteredOptions$, this.cappedFilteredOptions$]).pipe(
            map(([filteredOptions, cappedFilteredOptions]) => {
                return (this.search || this.autocomplete) && filteredOptions.length > cappedFilteredOptions.length;
            })
        );
    }

    protected override initListeners(): void {
        super.initListeners();
        this.initSelectedSuggestionsListener();
        this.initInitialAutocompleteOptionsListener();
        this.initExtraCountListener();
    }

    protected override onSearchTermChange(searchTerm: string): void {
        this.searchTerm.emit(searchTerm);
    }

    private buildFilteredOptionsStream(): Observable<FormSelectOption<T>[]> {
        // Don't show the selected items when a search term is entered
        const searchDependentState$: Observable<FormValue<FormSelectOption<T>>[]> = combineLatest([
            this.selectedState$,
            this.searchTerm$
        ]).pipe(
            map(([selectedState, searchTerm]) => {
                if (searchTerm.length > 0) {
                    return [];
                } else {
                    return selectedState;
                }
            })
        );

        // Merge the selected items with the stream of options
        const allSelectOptions$ = merge(this.multiFormSelectOptions$, this.initialAutocompleteOptions$);
        const mergedOptions$: Observable<MultiFormSelectOptions<T>> = combineLatest([
            searchDependentState$,
            allSelectOptions$
        ]).pipe(
            map(([searchSelectedState, multiFormSelectOptions]) => {
                const options: FormSelectOption<T>[] = multiFormSelectOptions.options;
                const unselected: FormSelectOption<T>[] = options.filter(
                    option => !searchSelectedState.find(el => isEqual(el.value.value, option.value))
                );

                const selected: FormSelectOption<T>[] = searchSelectedState.map(state => state.value);

                if (this.sortAlphabetically) {
                    selected.sort((a, b) => a.label.localeCompare(b.label));
                    unselected.sort((a, b) => a.label.localeCompare(b.label));
                }

                const mergedOptions: FormSelectOption<T>[] = [...selected, ...unselected];
                return { isLiveData: multiFormSelectOptions.isLiveData, options: mergedOptions };
            })
        );

        /* Only emit new options when the dropdown is closed or the options are updated */
        const throttledOptionRefresh$: Observable<MultiFormSelectOptions<T>> = combineLatest([
            this.isOpen$,
            this.multiFormSelectOptions$
        ]).pipe(
            map(([isOpen]) => isOpen),
            filter(isOpen => isOpen !== false),
            withLatestFrom(mergedOptions$),
            map(([_, mergedOptions]) => mergedOptions)
        );

        return combineLatest([this.searchTerm$, throttledOptionRefresh$]).pipe(
            map(([searchTerm, multiFormSelectOptions]: [string, MultiFormSelectOptions<T>]) => {
                const options: FormSelectOption<T>[] = multiFormSelectOptions.options;
                if (multiFormSelectOptions.isLiveData) {
                    return [...options];
                } else {
                    return [...this.filterCurrentSelectOptions(searchTerm, options)];
                }
            }),
            shareReplay(1)
        );
    }

    private filterCurrentSelectOptions(searchTerm: string, options: FormSelectOption<T>[]): FormSelectOption<T>[] {
        return options.filter((option: FormSelectOption<T>) =>
            option.label.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }

    private buildCappedFilteredOptionsStream(): Observable<FormSelectOption<T>[]> {
        return combineLatest([this.filteredOptions$, this.selectedSuggestions$, this.extraItemCount$]).pipe(
            map(([filteredOptions, selectedSuggestions, extraItemCount]) => {
                return this.search || this.autocomplete
                    ? this.combinedFilteredSelectedSuggestion(filteredOptions, selectedSuggestions).slice(
                          0,
                          this.itemCapCount + extraItemCount
                      )
                    : filteredOptions;
            })
        );
    }

    private combinedFilteredSelectedSuggestion(
        filteredOptions: FormSelectOption<T>[],
        selectedSuggestions: FormSelectOption<T>[]
    ): FormSelectOption<T>[] {
        if (selectedSuggestions.length === 0) {
            return filteredOptions;
        }
        return uniqWith([...filteredOptions, ...selectedSuggestions], isEqual);
    }

    private initExtraCountListener(): void {
        this.extraItemCount$.pipe(takeUntil(this.destroy$)).subscribe(extraItemCount => {
            this.shownItemCount.next(extraItemCount + this.itemCapCount);
        });
    }

    protected override initStateChangeListener(): void {
        this.state$.pipe(takeUntil(this.destroy$)).subscribe(state => this.selectionState.emit(state));
    }

    private initInitialAutocompleteOptionsListener(): void {
        this.initialAutocompleteOptions$.pipe(take(1), takeUntil(this.destroy$)).subscribe();
    }

    private initSelectedSuggestionsListener(): void {
        this.selectedSuggestions$
            .pipe(skip(1), withLatestFrom(this.state$), takeUntil(this.destroy$))
            .subscribe(([selectedSuggestions, state]: [FormSelectOption<T>[], FormValue<FormSelectOption<T>>[]]) => {
                if (this.multiple) {
                    const updatedState: FormValue<FormSelectOption<T>>[] = this.toggleSelectedSuggestions(
                        selectedSuggestions,
                        state
                    );
                    this.setState(updatedState);
                } else {
                    this.setState(selectedSuggestions.map(suggestion => new FormValue(suggestion)));
                }
            });
    }

    private toggleSelectedSuggestions(
        selectedSuggestions: FormSelectOption<T>[],
        state: FormValue<FormSelectOption<T>>[]
    ): FormValue<FormSelectOption<T>>[] {
        const newState: FormValue<FormSelectOption<T>>[][] = selectedSuggestions.map(
            (suggestionOption: FormSelectOption<T>) => {
                const isInCurrentState = !!state.find((stateEntry: FormValue<FormSelectOption<T>>) =>
                    isEqual(stateEntry.value.value, suggestionOption.value)
                );

                if (isInCurrentState) {
                    return state.map((stateEntry: FormValue<FormSelectOption<T>>) => {
                        const toggledState: FormValueState =
                            stateEntry.state === FormValueState.SET ? FormValueState.UNSET : FormValueState.SET;

                        return isEqual(stateEntry.value.value, suggestionOption.value)
                            ? new FormValue(suggestionOption, toggledState)
                            : stateEntry;
                    });
                } else {
                    return [...state, new FormValue(suggestionOption)];
                }
            }
        );

        const newStateFlat: FormValue<FormSelectOption<T>>[] = flatten(newState);
        return uniqWith(newStateFlat, isEqual);
    }

    protected override initHasOptionsStream(): void {
        this.hasOptions$ = this.cappedFilteredOptions$.pipe(
            take(1),
            map(() => true)
        );
    }

    // #region Autocomplete
    public receiveInitialAutocompleteOptions(autocompleteOptions: MultiFormSelectOptions<T>): void {
        this.initialAutocompleteOptions$.next(autocompleteOptions);
    }

    public receiveSelectedSuggestion(suggestions: FormSelectOption<T>[]): void {
        if (suggestions.length <= 0) {
            return;
        }
        this.selectedSuggestions$.next(suggestions);
        this.autocompleteInputControl.setValue('');
    }
    // #endregion

    // #region Load more
    protected override getLoadMoreLabel(): string {
        return this.listCappedMsg;
    }

    protected override onLoadMore(): void {
        this.extraItemCount$.next(this.extraItemCount$.value + Math.max(10, this.itemCapCount / 2));
    }
    // #endregion

    public onHoverSearch(): void {
        this.arrowNavigation.setSearchFocusIndex(-1);
    }
}
